import { onboardingPageClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';
import { INVENTORY_EXPLORER_HEIGHT } from '@/components/smartai/ComponentsList/components-page-layout';
import { cn } from '@/lib/utils';

const SkeletonBar = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'animate-pulse rounded-md bg-[var(--inv-surface-muted)]',
      className,
    )}
  />
);

const MetricCardSkeleton = ({ tall = false }: { tall?: boolean }) => (
  <div className="overflow-hidden rounded-[14px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]">
    <SkeletonBar className="h-1.5 w-full rounded-none" />
    <div className={cn('px-4', tall ? 'py-3' : 'py-3')}>
      <SkeletonBar className="mb-2 h-2.5 w-16" />
      <SkeletonBar className={cn('w-20', tall ? 'h-7' : 'h-5')} />
      {tall ? (
        <>
          <SkeletonBar className="mt-2 h-3 w-28" />
          <SkeletonBar className="mt-3 h-1.5 w-full rounded-full" />
        </>
      ) : null}
    </div>
  </div>
);

const SecondaryMetricSkeleton = () => (
  <div className="overflow-hidden rounded-[14px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]">
    <SkeletonBar className="h-1 w-full rounded-none" />
    <div className="flex items-center gap-2.5 px-3.5 py-3">
      <SkeletonBar className="h-2 w-2 shrink-0 rounded-[2px]" />
      <div className="min-w-0 flex-1">
        <SkeletonBar className="mb-1.5 h-2 w-12" />
        <SkeletonBar className="h-5 w-10" />
      </div>
    </div>
  </div>
);

export const InventoryCodePaneSkeleton = () => (
  <div className="flex flex-1 flex-col gap-2.5 overflow-hidden p-4">
    {Array.from({ length: 14 }).map((_, index) => (
      <SkeletonBar
        key={index}
        className={cn(
          'h-3',
          index % 4 === 0 ? 'w-[92%]' : index % 3 === 0 ? 'w-[78%]' : 'w-[65%]',
        )}
      />
    ))}
  </div>
);

const InventorySkeleton = () => (
  <div
    className={cn(onboardingPageClass, 'animate-pulse')}
    aria-busy="true"
    aria-label="Loading inventory">
    {/* Page header */}
    <header className="mb-5 flex items-start justify-between gap-4 max-[768px]:flex-col">
      <div className="max-w-[42rem] flex-1">
        <SkeletonBar className="mb-2 h-8 w-[min(100%,10rem)]" />
        <SkeletonBar className="h-4 w-[min(100%,32rem)]" />
      </div>
      <SkeletonBar className="h-10 w-40 shrink-0 rounded-[10px] max-[768px]:w-full" />
    </header>

    {/* Project chips */}
    <div className="mb-4 flex flex-wrap items-center gap-2.5">
      <SkeletonBar className="mr-1 h-3 w-14" />
      <SkeletonBar className="h-9 w-36 rounded-full" />
      <SkeletonBar className="h-9 w-44 rounded-full" />
    </div>

    {/* Metrics overview */}
    <section className="mb-6 flex flex-col gap-3" aria-hidden="true">
      <div className="grid grid-cols-1 gap-3 min-[720px]:grid-cols-2 min-[1100px]:grid-cols-3 min-[1536px]:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <MetricCardSkeleton key={index} tall={index === 2 || index === 3} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2.5 min-[900px]:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <SecondaryMetricSkeleton key={index} />
        ))}
      </div>
    </section>

    {/* File-level breakdown */}
    <section className="mt-2" aria-hidden="true">
      <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-4">
        <SkeletonBar className="h-6 w-44" />
        <SkeletonBar className="h-4 w-36" />
      </div>

      <div
        className={cn(
          'mb-5 grid grid-cols-1 gap-4 min-[960px]:grid-cols-[minmax(14rem,20rem)_minmax(0,1fr)]',
          INVENTORY_EXPLORER_HEIGHT,
        )}>
        {/* File list */}
        <aside className="overflow-hidden rounded-xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] min-[960px]:h-full">
          <div className="flex items-center justify-between border-0 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-3 py-2">
            <SkeletonBar className="h-3 w-10" />
            <SkeletonBar className="h-3 w-16" />
          </div>
          {Array.from({ length: 10 }).map((_, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-2 border-0 border-b border-solid border-[var(--inv-border-soft)] px-3 py-1.5">
              <SkeletonBar
                className={cn(
                  'h-3',
                  index % 3 === 0 ? 'w-[85%]' : index % 2 === 0 ? 'w-[70%]' : 'w-[55%]',
                )}
              />
              <SkeletonBar className="h-3 w-6 shrink-0" />
            </div>
          ))}
        </aside>

        {/* Code + structure */}
        <div className="flex min-h-0 min-w-0 flex-col gap-3 min-[960px]:h-full">
          <div className="flex h-[min(62vh,40rem)] shrink-0 flex-col overflow-hidden rounded-xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] min-[960px]:h-0 min-[960px]:min-h-[20rem] min-[960px]:flex-1">
            <div className="flex items-center justify-between gap-3 border-0 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-3 py-2">
              <SkeletonBar className="h-3 w-[min(100%,16rem)]" />
              <SkeletonBar className="h-3 w-24 shrink-0" />
            </div>
            <InventoryCodePaneSkeleton />
          </div>

          <div className="flex h-[min(11rem,18vh)] shrink-0 flex-col overflow-hidden rounded-xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)]">
            <div className="flex shrink-0 items-center justify-between border-0 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-4 py-2.5">
              <SkeletonBar className="h-3 w-36" />
              <SkeletonBar className="h-3 w-16" />
            </div>
            <div className="flex flex-1 flex-col gap-0 px-4 py-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between gap-3 border-0 border-b border-solid border-[var(--inv-border-soft)] py-2 last:border-b-0">
                  <SkeletonBar className="h-3 w-32" />
                  <SkeletonBar className="h-3 w-14 shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Tech stack panel placeholder */}
    <SkeletonBar className="h-24 w-full rounded-xl" />
  </div>
);

export default InventorySkeleton;
